import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { concatMap, filter, map, switchMap, tap } from 'rxjs';

import { OVERTIME_AUTOMATION_OVERRIDE_CODE } from '@shared-constants/cat-codes-constants';
import { DATE_FORMAT_TOR } from '@shared-constants/date-format-constants';
import { BasicInterfaceTypeEnum, TimesheetTypeEnum } from '@shared-enums/cats.enum';
import { SapBooleanEnum } from '@shared-enums/sap.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { arrayIsTruthy, findUnique, removeDuplicatesByProperty } from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment, getFilterString, isSameDay } from '@shared-helpers/date-helpers';
import {
  addDecimals,
  limitDecimalDisplay,
  safeCompare,
  toCurrencyString,
  trimLeadingZeros,
} from '@shared-helpers/string-number-helpers';
import * as sharedApi from '@shared-interfaces/api';

import { IVerticalBulkAddPerDiemValue } from '../components/modal/modal-content-vertical-bulk-add-per-diem/modal-content-vertical-bulk-add-per-diem.component';
import { ICatRecord } from '../interfaces/incoming';
import { ITableConfigEntity } from '../interfaces/internal-interfaces';
import { ISapRequestFilterQueryProps } from '../services/api.model';
import { DatahubService } from '../services/datahub.service';
import { ModalService } from '../services/modal.service';
import { IMixedCatsType, IOutgoingCatsArrays } from './catsDB';
import { ContractHandler } from './contracts/contract-handler';
import { Employee } from './employee';
import { Equipment } from './equipment';
import { ExtDocument } from './extDocument';
import { ProductionEquipment } from './productionEquipment';
import { ProductionQuantity } from './ProductionQuantity';
import { ProductionText } from './ProductionText';
import { payrollOverviewConfig } from './table-configs/payroll-tables.config';
import { ITimesheetDate, TimesheetHandler } from './timesheetHandler';

export interface IPayrollTotals {
  type: string;
  s: string | number;
  o: string | number;
  d: string | number;
  t: string;
}

export interface IPerDiemConfigItem {
  label: string;
  skf: string;
}

export class PayrollTimesheet extends TimesheetHandler {
  private readonly torAndRosterFinishedSubject$ = new BehaviorSubject(false);
  private readonly timeEvalFinishedSubject$ = new BehaviorSubject(false);

  public readonly torAndRosterFinished$ = this.torAndRosterFinishedSubject$.asObservable();
  public readonly timeEvalFinished$ = this.timeEvalFinishedSubject$.asObservable();

  public nonbillableExtDocument?: ExtDocument;
  public perDiemExtDocument?: ExtDocument;

  public readonly perDiemsConfig: ReadonlyArray<IPerDiemConfigItem> = [
    { label: 'Meal #1', skf: 'Z103' },
    { label: 'Meal #2', skf: 'Z104' },
    { label: 'Meal #3', skf: 'Z105' },
    { label: 'Hotel', skf: 'Z106' },
  ];

  public readonly timeEvalLoadingMessage = 'Loading time data...';
  public readonly timeEvalSavingMessage = 'Saving Changes...';

  private costCenters: string[] = [];
  private serviceOrders: ContractHandler[] = [];
  private torEntries: sharedApi.IPayrollTimeOffRequestResult[] = [];
  private payrollSimulation: sharedApi.IPayrollTimeEvalResult[] = [];
  private readonly torFinishedSubject$ = new BehaviorSubject(false);
  private readonly rosterFinishedSubject$ = new BehaviorSubject(false);

  constructor(datahub: DatahubService, modalService: ModalService) {
    super(datahub, modalService);
    this.display = `Payroll Timesheet Summary`;
    this.isPayroll = true;
    combineLatest([this.rosterFinishedSubject$, this.torFinishedSubject$, this.timeEvalFinished$])
      .pipe(filter((finishedValues) => finishedValues.every((isFinished) => isFinished))) //only continue if all behaviorSubjects are returning true
      .subscribe({
        next: () => {
          //Note: the payrollSimulation might be empty here, but that's ok. If it is then this is attempted again when the timeEval finishes loading
          this.assignEmployeeRecords(this.payrollSimulation);
          this.assignEquipmentRecords();
          this.determineTimesheetStatus();
          this.sort();
          this.torAndRosterFinishedSubject$.next(true);
        },
        error: (err) => {
          console.error('Error in combineLatest:', err);
        },
      });

    this.timeEvalFinished$
      .pipe(filter((isFinished) => isFinished)) //only continue if this behaviorSubject is true
      .subscribe({
        next: () => {
          this.assignEmployeeRecords(this.payrollSimulation);
        },
        error: (err) => {
          console.error('Payroll timesheet summary subscription error', err);
        },
      });
  }

  private getRecords(): void {
    // "get all cats records on these cost centers between the weekending date and the week starting date found in the datahub service with status 10, 20 or 30"
    // we can assume records in status 40 or above do not need any further data entry.
    this.costCenters = this.datahub.user.costCentersForApproval;
    this.applicableExtDocuments = [];
    this.torAndRosterFinishedSubject$.next(false);
    this.rosterFinishedSubject$.next(false);
    this.torFinishedSubject$.next(false);
    this.timeEvalFinishedSubject$.next(false);

    const selectString =
      'CatsHeadtoItem/Counter,CatsHeadtoItem/Workdate,CatsHeadtoItem/Employeenumber,CatsHeadtoItem/SendCctr,CatsHeadtoItem/RecCctr,CatsHeadtoItem/Acttype,CatsHeadtoItem/RecCctr,CatsHeadtoItem/RecOrder,CatsHeadtoItem/AbsAttType,CatsHeadtoItem/Catshours,CatsHeadtoItem/Shorttext,CatsHeadtoItem/Extdocumentno,CatsHeadtoItem/Status,CatsHeadtoItem/Statkeyfig,CatsHeadtoItem/Quantity,CatsHeadtoItem/Longtext,CatsHeadtoItem/Zequipment,CatsHeadtoItem/ZempCreatedBy,CatsHeadtoItem/Zmatnr,CatsHeadtoItem/ModifiedEmpno,CatsHeadtoItem/LongTextstr,CatsHeadtoItem/Zperdiemamount,CatsHeadtoItem/Zrecordtype';

    forkJoin([this.findUserExtDocuments$(), this.datahub.contracts.searchByCostCenter$(this.costCenters, this.weDate)])
      .pipe(
        tap(([documents, contracts]) => {
          this.extDocuments = documents.map((x) => new ExtDocument(x, this.datahub, false)); //find all timesheets owned by "workingAs"
          this.serviceOrders = contracts; //all the contracts in the cost center(s) by weekending date
        }),
        switchMap(() =>
          this.datahub.catsDB.getPayrollRecords$(this.extDocuments, this.datahub.user.reports, this.weDate, selectString),
        ),
        tap((values) => {
          const filteredValues = this.filterRecords(values, this.extDocuments, this.datahub.user.reports);
          this.employeeRecords = filteredValues.employees;
          this.equipmentRecords = filteredValues.equipment;
          this.productionQuantityRecords = filteredValues.productionQuantity;
          this.productionTextRecords = filteredValues.productionText;
          this.catsRecords = filteredValues.all;
          this.applicableExtDocuments = this.extDocuments;
          this.productionQuantity = this.determineQuantityProduction(this.serviceOrders, this.productionQuantityRecords);
          this.productionText = this.determineTextProduction(this.serviceOrders, this.productionTextRecords);
        }),
        switchMap(() => this.getTemporalRoster$(this.employeeRecords, this.equipmentRecords)),
        tap(([employees, equipment]) => {
          this.equipment = equipment.map((equipmentItem) => new Equipment(equipmentItem, this.datahub, 'payroll'));
          this.productionEquipment = this.buildEquipmentSkfRoster(this.serviceOrders, this.productionQuantityRecords);
          this.employees = employees.map(
            (employeeItem) => new Employee(employeeItem, this.datahub, this.modalService, 'payroll'),
          );
          this.rosterFinishedSubject$.next(true);
        }),
        switchMap(() => this.findTorRecords$(this.datahub.user.reports)),
        tap((torList) => {
          const employeesWithoutTor = torList.filter((x) => x.TorResult?.results != null && x.TorResult.results.length > 0);
          const filtered = employeesWithoutTor.filter((x) => !!this.employees.find((y) => safeCompare(y.EmpId, x.Pernr)));
          this.torEntries = filtered
            .flatMap((torEmployee) =>
              this.employees.find((x) => safeCompare(x.EmpId, torEmployee.Pernr))?.handleTorRecordsSet(torEmployee),
            )
            .filter((x) => x != null);
          this.torFinishedSubject$.next(true);
        }),
        switchMap(() => this.getPayrollSimulation$(this.employeeRecords, this.torEntries)),
        tap((simulationResult) => {
          this.payrollSimulation = simulationResult;
          this.timeEvalFinishedSubject$.next(true);
        }),
        switchMap(() => this.findExtDocuments$()),
      )
      .subscribe({
        error: (err) => {
          console.error('Error in getRecords:', err);
        },
      });
  }

  public addDocument(entity: Employee | Equipment | ProductionEquipment | ProductionQuantity | ProductionText): void {
    let timesheets = this.applicableExtDocuments.filter((doc) => doc.isBillable());

    //Exclude timesheets that do not have a valid/matching SKF
    if (
      entity.billCodeType === BasicInterfaceTypeEnum.ProductionEquipment ||
      entity.billCodeType === BasicInterfaceTypeEnum.ProductionQuantity ||
      entity.billCodeType === BasicInterfaceTypeEnum.ProductionText
    ) {
      timesheets = timesheets.filter((doc) => doc.contractDetails?.doesContainSkf(entity.SKF));
    }

    //there is no focused day on the bulk add. but if we are on a day of the week we will filter out invalid contracts by date
    const entityFocusedDay = entity.focusedDay;
    if (entityFocusedDay != null) {
      timesheets = timesheets.filter((doc) => doc.contractDetails?.isValidForDate(entityFocusedDay.moment));
    }

    const data = {
      step: 0,
      timesheets,
      type: 'payroll',
      handler: this.weDate,
    };

    this.modalService
      .launchResponsiveModal('timesheet-select', data)
      .afterClosed()
      .pipe(filter((selectedDocument: ExtDocument | undefined) => selectedDocument != null))
      .subscribe({
        next: (selectedDocument) => {
          if (
            (entity.billCodeType === BasicInterfaceTypeEnum.ProductionEquipment ||
              entity.billCodeType === BasicInterfaceTypeEnum.ProductionQuantity ||
              entity.billCodeType === BasicInterfaceTypeEnum.ProductionText) &&
            !selectedDocument.contractDetails?.doesContainSkf(entity.SKF)
          ) {
            //Run the same check again after closing the modal since it might be possible to manually choose an invalid one from the "find other timesheets" button
            this.datahub.messageService.error(
              `That timesheet cannot be selected because is not set up to allow <strong>${entity.SKFDescription}</strong> to be entered`,
            );
            return;
          }
          this.addExtDoc(selectedDocument);
          this.datahub.detailPayneHandler.addDocumentToView(selectedDocument, entity.getPayrollDetailPayneConfig());
        },
        error: (err) => {
          console.error('Error in addDocument => afterClosed subscription:', err);
        },
      });
  }

  private findTorRecords$(employees: string[]): Observable<sharedApi.IPayrollTimeOffRequestResultSetResult[]> {
    const torObj: sharedApi.IHttpRequestPayrollTimeOffRequest = {
      StartDate: this.weDate.getWeekStartingDate().format(DATE_FORMAT_TOR),
      EndDate: this.weDate.getMoment().format(DATE_FORMAT_TOR),
      EmployeeSet: employees.map((x) => {
        return { Pernr: x };
      }),
      ResultSet: [
        {
          Pernr: '',
          TorResult: [],
        },
      ],
    };
    return this.datahub.apiService.getTorEntries$(torObj);
  }

  private buildEquipmentSkfRoster(serviceOrders: ContractHandler[], catsRecords: ICatRecord[]): ProductionEquipment[] {
    const equipmentSkfs: ProductionEquipment[] = [];
    serviceOrders.forEach((order) => {
      const costCenter = order.getCostCenter();
      order.__equipmentSKF.forEach((skf) => {
        const found = equipmentSkfs.find((x) => x.SKF === skf.SKF);
        if (!found) {
          const records = catsRecords.filter((x) => x.Statkeyfig === skf.SKF); // find all the records that have the SKF
          const equipmentMimic = new ProductionEquipment(this.datahub, skf, 'payroll');
          equipmentMimic.setCatsRecords(records);
          equipmentMimic.addCostCenter(costCenter);
          equipmentSkfs.push(equipmentMimic);
        } else if (!found.costCenters.find((x) => x === costCenter)) {
          found.addCostCenter(costCenter);
        }
      });
    });
    return equipmentSkfs;
  }

  public getPayrollSimulation$(
    employeeRecords: ICatRecord[],
    torEntries: sharedApi.IPayrollTimeOffRequestResult[],
  ): Observable<sharedApi.IPayrollTimeEvalResult[]> {
    const purifiedRecords = this.purifyRecordsForTimeEval(employeeRecords, this.datahub.user.reports);
    const employees = findUnique<string>(purifiedRecords, 'Employeenumber');
    const data: sharedApi.IHttpRequestPayrollTimeEval = {
      StartDate: this.weDate.getWeekStartingDate().format(DATE_FORMAT_TOR),
      EndDate: this.weDate.getMoment().format(DATE_FORMAT_TOR),
      EmployeeSet: employees.map((x) => {
        return { Pernr: x };
      }),
      Ignore: 'ALL',
      Wage: 'PAY',
      ItemsSet: [
        ...purifiedRecords.map((x) => this.catsEntryToTimeEval(x)),
        ...torEntries.map((x) => this.torEntriesToTimeEval(x)),
      ],
      ResultSet: [{ Pernr: '', TimeEvalResultSet: [] }],
    };
    return this.datahub.apiService.getTimeEval$(data);
  }

  public updateTimesheetEmployeeRecordsFromLocalInterface(localCatsRecords: ICatRecord[], employeeID: string): void {
    this.employeeRecords = this.employeeRecords.filter((x) => x.Employeenumber !== employeeID); //destroy all employee records for this entity
    this.employeeRecords = this.employeeRecords.concat(localCatsRecords); //sync timesheet to local
  }

  private determineTextProduction(contracts: ContractHandler[], catsRecord: ICatRecord[]): ProductionText[] {
    const productionText: ProductionText[] = [];
    contracts.forEach((contract) => {
      if (!contract.__areConditionsFetched) return;
      const costCenter: string = contract.getCostCenter();
      contract.__textSKF.forEach((textSKF) => {
        const found = productionText.find((pt) => pt.SKF === textSKF.SKF);
        if (found == null) {
          const text = new ProductionText(this.datahub, textSKF, 'payroll');
          text.addCostCenter(costCenter);
          productionText.push(text);
        } else if (!found.costCenters.some((cc) => cc === costCenter)) {
          found.addCostCenter(costCenter);
        }
      });
    });
    productionText.forEach((skf) => {
      const records = catsRecord.filter((x) => x.Statkeyfig === skf.SKF);
      skf.setCatsRecords(records);
    });
    return productionText;
  }

  private determineQuantityProduction(contracts: ContractHandler[], catsRecord: ICatRecord[]): ProductionQuantity[] {
    const productionQuantity: ProductionQuantity[] = [];
    contracts.forEach((contract) => {
      //determine all the entities that belong in the view
      if (!contract.__areConditionsFetched) return;
      const costCenter: string = contract.getCostCenter();
      contract.__quantitySKF.forEach((quantitySKF) => {
        const found = productionQuantity.find((pq) => pq.SKF === quantitySKF.SKF);
        if (found == null) {
          const quantity = new ProductionQuantity(this.datahub, quantitySKF, 'payroll');
          quantity.addCostCenter(costCenter);
          productionQuantity.push(quantity);
        } else if (!found.costCenters.some((cc) => cc === costCenter)) {
          found.addCostCenter(costCenter);
        }
      });
    });
    productionQuantity.forEach((skf) => {
      const records = catsRecord.filter((x) => x.Statkeyfig === skf.SKF);
      skf.setCatsRecords(records);
    });
    return productionQuantity;
  }

  private filterRecords(records: IMixedCatsType, documents: ExtDocument[], reports: string[]): IMixedCatsType {
    records.employees = records.employees.filter((record) => {
      if (reports.some((employee) => safeCompare(record.Employeenumber, employee))) return true;
      if (documents.some((document) => safeCompare(document.Documentno, record.Extdocumentno))) return true;
      return false;
    });
    return records;
  }

  private getTemporalRoster$(
    employeeRecords: ICatRecord[],
    equipmentRecords: ICatRecord[],
  ): Observable<[sharedApi.IHttpResponseEmployee[], sharedApi.IHttpResponseEquipment[]]> {
    const employeesInRecords = findUnique<string>(employeeRecords, 'Employeenumber');
    let empNos = employeesInRecords.concat(this.datahub.user.reports);
    empNos = findUnique<string>(empNos, '');
    const foreignEqpRecords = equipmentRecords.filter(
      (equipRec) => !this.costCenters.includes(trimLeadingZeros(equipRec.SendCctr)),
    );
    const eqpNos = findUnique<string>(foreignEqpRecords, 'Zequipment').map((x) => trimLeadingZeros(x));
    const foreignEqpParams = {
      ValidTo: getFilterString(this.weDate.getWeekStartingDate()),
      EquipmentId: eqpNos,
    };

    const empParams = {
      EmpId: empNos,
      WeDate: this.weDate.getFilterString(), //Have to use the week starting date in case they get termed midweek
    };

    const eqpParams = {
      CostCenter: this.datahub.user.costCentersForApproval,
      ValidTo: getFilterString(this.weDate.getWeDate()),
    };
    let emps$ = this.datahub.apiService.getEmployees$(empParams);
    if (!arrayIsTruthy(empNos)) emps$ = of([]);
    let eqps$ = this.datahub.apiService.getEquipment$(eqpParams).pipe(
      // temporary fix, when item transfers mid week, we need to re evaluate it by equipment number to get REAL invalid dates.
      map((equipments) => {
        equipments.forEach((equipment) => {
          if (equipment.CCWeek.split(',').length < 7) {
            const singleEqpParams = {
              EquipmentId: equipment.EquipmentId,
              ValidTo: getFilterString(this.weDate.getWeDate()),
            };
            this.datahub.apiService.getOneEquipment$(singleEqpParams).subscribe({
              next: (newEquipment) => {
                equipment.InvalidDates = newEquipment?.InvalidDates ?? '';
              },
              error: (err) => {
                console.error('Error in getTemporalRoster:', err);
              },
            });
          }
        });
        return equipments;
      }),
    );
    // if(arrayIsTruthy(foreignEmpRecords)) emps = combineAndMergeObservables(emps, this.datahub.apiService.getEmployees(foreignEmpParams))
    if (arrayIsTruthy(foreignEqpRecords)) {
      eqps$ = this.combineAndMergeObservables$(eqps$, this.datahub.apiService.getEquipment$(foreignEqpParams)).pipe(
        map((x) => removeDuplicatesByProperty(x, 'EquipmentId')),
      );
    }
    return forkJoin([emps$, eqps$]);
  }

  public reset(): void {
    this.timesheetStatus = {
      number: '',
      description: 'Searching..',
      shouldPermitPayrollApproval: false,
      shouldPermitBillingApproval: false,
      error: '',
    };
    this.getRecords();
  }

  public navigateToSelf(approver?: string): void {
    let url = '';
    if (approver) url = `/payroll?wedate=${this.weDate.getSapWeDateString()}&approver=${approver}`;
    else url = `/payroll?wedate=${this.weDate.getSapWeDateString()}&approver=${this.datahub.user.approvingId}`;
    this.datahub.navigate(url);
  }

  public getConfig(): ITableConfigEntity[] {
    return payrollOverviewConfig;
  }

  public getValues(): IPayrollTotals[] {
    return [this.findEmployeeTotals(), this.findEquipmentTotals(), this.findPerDiemTotals()];
  }

  private findEmployeeTotals(): IPayrollTotals {
    let s: string | number = 0;
    let o: string | number = 0;
    let d: string | number = 0;
    this.employees.forEach((employee: Employee) => {
      s = addDecimals(s, employee.payrollTotals.s.total);
      o = addDecimals(o, employee.payrollTotals.o.total);
      d = addDecimals(d, employee.payrollTotals.d.total);
    });
    return this.makeSummaryRow(s, o, d, 'Employees');
  }

  private findEquipmentTotals(): IPayrollTotals {
    let s = '0';
    const allEquipment = [...this.equipment, ...this.productionEquipment];
    allEquipment.forEach((equipment) => {
      s = addDecimals(s, equipment.payrollTotal);
    });
    return this.makeSummaryRow(s, '', '', 'Equipment');
  }

  private findPerDiemTotals(): IPayrollTotals {
    const row = this.makeSummaryRow('', '', '', 'Per Diem');
    const totalPerDiem = this.employees
      .map((employee: Employee) =>
        //Total the per diems for each employee
        employee.perDiemRecords.reduce((prevPdTotal, currPd) => addDecimals(prevPdTotal, currPd.Zperdiemamount), '0'),
      )
      //Now total above per-employee totals into a grand total
      .reduce((prevRowTotal, curr) => addDecimals(prevRowTotal, curr), '0');

    row.t = toCurrencyString(totalPerDiem, true);
    return row;
  }

  private makeSummaryRow(
    standard: string | number,
    over: string | number,
    double: string | number,
    type: string,
  ): IPayrollTotals {
    let t = addDecimals(standard, over);
    t = addDecimals(t, double);
    return {
      type: type,
      s: standard,
      o: over,
      d: double,
      t: t,
    };
  }

  public updateExtDocumentStatus(documentNumbers: string[], type: string): void {
    //if the timesheet contains labor hours, set it back to IP no matter what. If it does not, lower the status to PA. Logic is different between payroll and billing.
    if (!arrayIsTruthy(documentNumbers)) return; //do nothing if no timesheets were updated.
    let target: TimesheetStatusEnum = TimesheetStatusEnum.InProgress;
    if (type !== 'em') target = TimesheetStatusEnum.PayrollApproved;
    const joinedDocuments = documentNumbers.map((docNum) => this.extDocuments.find((doc) => safeCompare(doc.Documentno, docNum)));
    joinedDocuments.forEach((document) => {
      if (document != null) {
        if (document.Status === TimesheetStatusEnum.InProgress || document.Status === target) return; //nothing to do if already in lowest category, or is at the target
        if (document.Status === TimesheetStatusEnum.Created) document.Status = TimesheetStatusEnum.InProgress;
        else document.Status = target;
        document.updateSelf(document.Status);
      }
    });
  }

  public bulkAddPerDiemForShownEmployees(selectedDate: ITimesheetDate, bulkAddValues: IVerticalBulkAddPerDiemValue[]): void {
    this.datahub.detailPayneHandler.isWorking = true;

    this.findPerDiemTimesheet$(this.datahub.user.approvingId)
      .pipe(
        switchMap(() => {
          const applicableEmployees = this.employees.filter(
            (emp) => !!emp.shouldShow && this.datahub.user.isDirectReport(emp.EmpId),
          );

          const outgoingRecords: IOutgoingCatsArrays = {
            create: [],
            update: [],
            delete: [],
            all: [],
          };

          // Gather the data we are about to save
          applicableEmployees.forEach((emp) => {
            bulkAddValues.forEach((perDiemConfig) => {
              const existingPerDiemOnEmployee = emp.perDiemRecords.find(
                (pd) =>
                  pd.Statkeyfig === perDiemConfig.skf && isSameDay(convertSapDateToMoment(pd.Workdate), selectedDate.moment),
              );
              if (existingPerDiemOnEmployee) {
                // This per diem already exists for this employee, so we will need to add the values together and update
                const newAmount = addDecimals(existingPerDiemOnEmployee.Zperdiemamount, perDiemConfig.perDiemAmount);

                outgoingRecords.update.push({
                  Counter: existingPerDiemOnEmployee.Counter,
                  Zperdiemamount: newAmount,
                });
              } else if (this.perDiemExtDocument != null && Number(perDiemConfig.perDiemAmount) > 0) {
                // This per diem does not yet exist on this employee, so we will create a new one
                // Don't create zero amount records though!
                const record = emp.addNewPerDiemRecord(this.perDiemExtDocument.Documentno, selectedDate);
                record.Statkeyfig = perDiemConfig.skf;
                record.Zperdiemamount = perDiemConfig.perDiemAmount; // ensure we always have a number string with decimal places, even when it's an empty string
                outgoingRecords.create.push(record.getCatsFormattedRecord());
              }
            });
          });

          // Save the data
          return this.datahub.catsDB.syncSingleEntityWithCats$(outgoingRecords);
        }),
        tap((newRecords) => {
          // Make the UI respond to the new data changes
          newRecords.forEach((record) => {
            // Either update the existing record in memory OR add it into the array as a new one
            const foundRecordByCounter = this.employeeRecords.find(
              (x) => safeCompare(record.Counter, x.Counter) || safeCompare(record.Refcounter, x.Counter),
            );
            if (foundRecordByCounter) {
              foundRecordByCounter.Zperdiemamount = limitDecimalDisplay(record.Zperdiemamount);
              foundRecordByCounter.Status = record.Status;
            } else {
              this.employeeRecords.push(record);
            }
          });

          const documentNos = newRecords.map((x) => x.Extdocumentno);
          this.updateExtDocumentStatus(documentNos, 'em');
          this.determineTimesheetStatus();
          this.assignEmployeeRecords();
          this.datahub.detailPayneHandler.refreshTimesheet(); // this will reset the loading status in the UI as well
        }),
      )
      .subscribe({
        error: (err) => {
          console.error('Error in bulkAddPerDiemForShownEmployees:', err);
        },
      });
  }

  public setNonbillTimesheet(user: string, extDocuments: ExtDocument[]): void {
    const nonbill = extDocuments.find((x) => x.isNonBill() && !x.isPerDiem() && safeCompare(x.Owner, user));
    if (nonbill) this.datahub.payrollTimesheet.nonbillableExtDocument = nonbill;
    else {
      const body = {
        Owner: user,
        Dateentered: this.weDate.getUnixString(),
        Pernr: user,
        Description: `${this.datahub.user.approvingName} Nonbillable Timesheet`,
        Costcenter1: this.datahub.user.approvingCostCenter,
        Nonbillable: SapBooleanEnum.True,
        Type: TimesheetTypeEnum.Nonbillable,
        Status: TimesheetStatusEnum.Created,
      };
      this.datahub.apiService
        .makeTimesheet$(body)
        .pipe(
          tap((x) => {
            const document = new ExtDocument(x, this.datahub, false);
            this.extDocuments.push(document);
            this.datahub.payrollTimesheet.nonbillableExtDocument = document;
          }),
        )
        .subscribe({
          error: (err) => {
            console.error('Error creating nonbillable timesheet:', err);
          },
        });
    }
  }

  public findPerDiemTimesheet$(
    approvingUserId: string,
    extDocuments: ExtDocument[] = this.extDocuments,
  ): Observable<ExtDocument> {
    const perDiem = extDocuments.find((x) => x.isPerDiem() && safeCompare(x.Owner, approvingUserId));
    if (perDiem) {
      this.perDiemExtDocument = perDiem;
      return of(perDiem);
    }

    const body: ISapRequestFilterQueryProps = {
      Owner: approvingUserId,
      Dateentered: this.weDate.getUnixString(),
      Pernr: approvingUserId,
      Description: `${this.datahub.user.approvingName} Per Diem Timesheet`,
      Costcenter1: this.datahub.user.approvingCostCenter,
      Nonbillable: SapBooleanEnum.True,
      Type: TimesheetTypeEnum.PerDiem,
      Status: TimesheetStatusEnum.Created,
    };
    return this.datahub.apiService.makeTimesheet$(body).pipe(
      map((x) => {
        const document = new ExtDocument(x, this.datahub, false);
        this.extDocuments.push(document);
        return document;
      }),
      tap((x) => {
        this.perDiemExtDocument = x;
      }),
    );
  }

  private combineAndMergeObservables$(obs$: Observable<any[]>, obs2$: Observable<any[]>): Observable<any[]> {
    return obs$.pipe(concatMap((obs1Result) => obs2$.pipe(map((obs2Result) => obs2Result.concat(obs1Result)))));
  }

  private torEntriesToTimeEval(torEntry: sharedApi.IPayrollTimeOffRequestResult): sharedApi.IPayrollTimeEvalOutgoingRecord {
    //todo: call tor api to see how to bind to actual data properties.
    return {
      Pernr: torEntry.Pernr,
      Date: torEntry.Begda,
      Code: torEntry.Leavetype,
      Hrs: torEntry.Abshours,
    };
  }

  private catsEntryToTimeEval(catsRecord: ICatRecord): sharedApi.IPayrollTimeEvalOutgoingRecord {
    return {
      Pernr: catsRecord.Employeenumber,
      Date: convertSapDateToMoment(catsRecord.Workdate).format(DATE_FORMAT_TOR),
      Code: catsRecord.AbsAttType,
      Hrs: catsRecord.Catshours,
    };
  }

  private purifyRecordsForTimeEval(records: ICatRecord[], reports: string[]): ICatRecord[] {
    let recordsMinusOtAuto = records.filter((x) => x.AbsAttType !== OVERTIME_AUTOMATION_OVERRIDE_CODE); //remove the pay as recorded flag from the totals
    recordsMinusOtAuto = recordsMinusOtAuto.filter((x) => Number(x.Catshours) > 0); // remove empty records to prevent getting back chaff
    return recordsMinusOtAuto.filter((catRecord) => {
      //DTD-420 only domestic employees run time eval
      return reports.some((employeeID) => safeCompare(catRecord.Employeenumber, employeeID));
    });
  }
}
